import { defineStore } from 'pinia';
import { ref } from 'vue';

import { DashboardAPI } from '@/apis';
import { MULTI_CHANNEL_CONTENT_KEY, MULTI_CHANNEL_KEY } from '@/models/dashboards/channels.enum';
import { logger } from '@/utils/logger';

import { useFlagStore } from './flag';

// TODO: Move metric types and comparable metrics into this store
export const useMetricsStore = defineStore('metrics', () => {
  const metricDetails = ref({});
  const metricDetailsReady = ref(false);
  const metricDetailsUsingLegacyCopy = ref(null);
  const metricDetailsAbortController = ref(new AbortController());

  const legacyMetricDetails = ref({});
  const legacyMetricDetailsReady = ref(false);

  async function fetchMetricDetails({ legacyCopy = false } = {}) {
    if (metricDetailsUsingLegacyCopy.value === legacyCopy) {
      return;
    }

    metricDetailsAbortController.value?.abort();
    metricDetailsAbortController.value = new AbortController();
    metricDetailsReady.value = false;
    metricDetailsUsingLegacyCopy.value = legacyCopy;

    try {
      const response = await DashboardAPI.getMetricDetails(
        { legacyCopy },
        { signal: metricDetailsAbortController.value.signal },
      );
      metricDetails.value = response?.data;
      metricDetailsReady.value = true;
    } catch (err) {
      logger.error('Failed to fetch metric details', {}, err);
    }
  }

  function getMetricKeysByParent(
    channel,
    metricReportTypeKey,
    parentMetric,
    includeHidden = false,
  ) {
    const flagStore = useFlagStore();
    const allChannelTypeMetrics = metricDetails.value?.[channel]?.[metricReportTypeKey];
    return Object.keys(allChannelTypeMetrics).filter((metricKey) => {
      const {
        parent_metric: currentParentMetric,
        hide_on_dashboards: hideOnDashboards,
        required_feature_flag: requiredFeatureFlag,
      } = allChannelTypeMetrics[metricKey];
      const parentMetricMatch = metricKey === parentMetric || currentParentMetric === parentMetric;
      const hasRequiredFlag = Boolean(
        !requiredFeatureFlag || (flagStore.ready && flagStore.flags?.[requiredFeatureFlag]),
      );
      return (includeHidden || !hideOnDashboards) && parentMetricMatch && hasRequiredFlag;
    }, {});
  }

  function getTargetMetricDetails(channel, metricReportTypeKey, metric) {
    return metricDetails.value?.[channel]?.[metricReportTypeKey]?.[metric];
  }

  function getTargetMetricDetailsMultiChannel(metric, isContentReport = false) {
    if (isContentReport) return metricDetails.value?.[MULTI_CHANNEL_CONTENT_KEY]?.[metric];
    return metricDetails.value?.[MULTI_CHANNEL_KEY]?.[metric];
  }

  /**
   * NOTE: This hopefully will be a temporary solution until we can move custom metrics to the new
   * metric details format. Do not propagate this pattern if possible.
   */
  async function initializeLegacyMetricDetails() {
    if (!legacyMetricDetails.value) return;
    const response = await DashboardAPI.getMetricDetails({ legacyCopy: true });
    legacyMetricDetails.value = response?.data;
    legacyMetricDetailsReady.value = true;
  }

  return {
    metricDetailsReady,
    metricDetails,
    fetchMetricDetails,
    getMetricKeysByParent,
    getTargetMetricDetails,
    getTargetMetricDetailsMultiChannel,
    initializeLegacyMetricDetails,
    legacyMetricDetails,
    legacyMetricDetailsReady,
  };
});
